import { useLocation } from '@tanstack/react-router';
import type { OnChangeFn, Row, RowSelectionState } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { create } from 'zustand';

type RouteSelectionState = {
  selections: { [routeKey: string]: string | null };
  setSelection: (routeKey: string, rowId: string | null) => void;
};

const useRouteSelectionStore = create<RouteSelectionState>((set) => ({
  selections: {},
  setSelection: (routeKey: string, rowId: string | null) =>
    set((state) => ({
      selections: { ...state.selections, [routeKey]: rowId },
    })),
}));

/**
 * Hook to manage TanStack Table row selection with route-based isolation.
 * Each route maintains its own selected row, persisted across navigation.
 *
 * @param keySuffix - Optional suffix to create separate selections within the same route
 *                    (e.g., 'buyup' vs 'regular' views on the same page)
 *
 * Returns props that can be spread directly to TanStackTable.
 */
export function useRouteRowSelection(keySuffix?: string) {
  const location = useLocation();
  const { selections, setSelection } = useRouteSelectionStore();

  // Build the selection key from pathname and optional suffix
  const selectionKey = keySuffix ? `${location.pathname}:${keySuffix}` : location.pathname;

  // Convert route-based selection to TanStack RowSelectionState
  const rowSelection: RowSelectionState = useMemo(() => {
    const selectedRowId = selections[selectionKey];
    if (selectedRowId) {
      return { [selectedRowId]: true };
    }

    return {};
  }, [selections, selectionKey]);

  // Handle selection changes from TanStack
  const onRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
    (updaterOrValue) => {
      const newSelection =
        typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue;

      // Get the selected row ID (single selection)
      const selectedIds = Object.keys(newSelection).filter((id) => newSelection[id]);
      const selectedId = selectedIds.length > 0 ? selectedIds[0] : null;

      setSelection(selectionKey, selectedId);
    },
    [selectionKey, rowSelection, setSelection]
  );

  // Single-select toggle: clicking an already-selected row clears it.
  const onRowClick = useCallback(
    <T>(row: Row<T>) => {
      if (row.getIsSelected()) {
        setSelection(selectionKey, null);
      } else {
        setSelection(selectionKey, row.id);
      }
    },
    [selectionKey, setSelection]
  );

  return {
    rowSelection,
    onRowSelectionChange,
    onRowClick,
  };
}
